import store from '@/store'
import { ref, watch, getCurrentInstance, computed } from '@vue/composition-api'
import { skutopiaRequest, foundryRequest } from '@/config/authConfig';
import { mdiEarth, mdiShape, mdiTagOutline, mdiBriefcaseVariant } from '@mdi/js'

export default function useProduct() {
    const vm = getCurrentInstance().proxy
    
    const productsData = ref([])
    const totalProducts = ref(0)
    const loadProductData = ref(false)
    const downloadLoading = ref(false)

    const IMAGE_MAPPING_SIDE = {
      "1": "FRONT",
      "2": "LEFT45",
      "3": "LEFT",
      "4": "LEFT135",
      "5": "BACK",
      "6": "RIGHT135",
      "7": "RIGHT",
      "8": "RIGHT45",
      "9": "BOTTOM",
      "10": "TOP",
      "11": "DETAIL",
      "12": "OTHER",
      "FRONT": 1,
      "LEFT45":2,
      "LEFT":3,
      "LEFT135":4,
      "BACK":5,
      "RIGHT135":6,
      "RIGHT":7,
      "RIGHT45":8,
      "BOTTOM":9,
      "TOP":10,
      "DETAIL":11,
      "OTHER":12,
  }

    const product = ref({})
    
    // Data table options
    const options = ref({
      sortBy: ['id'],
      sortDesc: [true],
      orderBy:'',
      page: 1,
      itemsPerPage: 25 //parseInt(process.env.VUE_APP_ITEMS_PER_PAGE)
    })

    const searchQuery = ref('')
    const selectedCategories = ref([]) 
    const selectedMarkets = ref([])
    const selectedProjects = ref([])
    const selectedBrands = ref([])
    const selectedClients = ref([])
    
    const facets = ref({
      categories: [],
      markets: [],
      projects: [],
      brands: [],
      clients: [], 
      total_count: 0
    })


    const marketOptions = ref([])
    const marketLoading = ref(false)

    const categoryOptions = ref([])
    const categoryLoading = ref(false)

    const brandOptions = ref([])
    const brandLoading = ref(false)

    const specOptions = ref([])
    const specLoading = ref(false)


    const BulkProductsLoading = ref(false)

    const getTargetRef = (type) => {
      switch (type) {
        case 'categories': return selectedCategories
        case 'markets': return selectedMarkets
        case 'brands': return selectedBrands
        default: return null
      }
    }

    const labelCache = ref({}) 

    const getLabelForValue = (type, value) => {
      const list = facets.value[type] || [];
      const found = list.find(item => item.value == value);
     
      if (found) {
        labelCache.value[`${type}_${value}`] = found.label
        return found.label
        }
        
      if (labelCache.value[`${type}_${value}`]) {
          return labelCache.value[`${type}_${value}`]
      }

      return value; 
  }

    const getFacetOptions = (type) => {
        const apiList = facets.value[type] || [];
        const selectedIds = getTargetRef(type).value || [];

        const missingSelected = selectedIds.filter(id => !apiList.find(i => i.value == id));

        const restoredItems = missingSelected.map(id => ({
            value: id,
            label: getLabelForValue(type, id),
            count: 0 
        }));

        return [...restoredItems, ...apiList];
    }

    const formatLabel = (defaultLabel, selectedArray, type) => {
      if (!selectedArray || selectedArray.length === 0) return defaultLabel;
      
      const names = selectedArray.map(val => getLabelForValue(type, val));
        
      const tags = names.map(s => `@${s}`).join(' ');
      
      if (tags.length > 25) {
          return `${defaultLabel} (${selectedArray.length} active)`;
      }
      return `${defaultLabel} ${tags}`;
    }

    const summaryCards = computed(() => {
        return [
            {
                key: 'markets',
                label: formatLabel('Markets', selectedMarkets.value, 'markets'),
                count: selectedMarkets.value.length > 0 ? selectedMarkets.value.length : facets.value.markets.length, 
                icon: mdiEarth,
                color: 'success'
            },
            {
                key: 'categories',
                label: formatLabel('Categories', selectedCategories.value, 'categories'),
                count: selectedCategories.value.length > 0 ? selectedCategories.value.length : facets.value.categories.length,
                icon: mdiShape,
                color: 'primary'
            },
            {
                key: 'brands',
                label: formatLabel('Brands', selectedBrands.value, 'brands'),
                count: selectedBrands.value.length > 0 ? selectedBrands.value.length : facets.value.brands.length,
                icon: mdiTagOutline,
                color: 'warning'
            },
        ]
    })

  

    const resetFilters = () => {
      selectedCategories.value = []
      selectedMarkets.value = []
      selectedProjects.value = []
      selectedBrands.value = []
      selectedClients.value = []
      productsData.value = []
      searchQuery.value = ''

      options.value.page = 1
  
      fetchProducts()
    } 

    const fetchFacets = async (paramsQuery) => {
        return store
            .dispatch('app-skutopia/fetchProductFacets', paramsQuery)
            .then(response => {
              facets.value = response.data
          })
            .catch(err => console.error("Error fetching facets", err))
    }

    const exportCatalog = async () => {
      

      const params = {
          search: searchQuery.value,
          category: selectedCategories.value.join(','),
          market: selectedMarkets.value.join(','),
          client: selectedClients.value.join(','),
          brand: selectedBrands.value.join(',') 
      }

      downloadLoading.value = true 
      store
          .dispatch('app-skutopia/exportCatalog', params)
          .then(response => {

              const url = window.URL.createObjectURL(new Blob([response.data]));
              const link = document.createElement('a');
              link.href = url;
              
              const date = new Date().toISOString().slice(0,10);
              link.setAttribute('download', `catalog_export_${date}.xlsx`);
              
              document.body.appendChild(link);
              link.click();
              
              link.remove();
              window.URL.revokeObjectURL(url);
          })
          .catch(error => {
              console.error("Export failed:", error)
          })
          .finally(() => {
              downloadLoading.value = false
          })
    }

    const fetchProducts = async (params={}) => {
      
      loadProductData.value = true

      const paramsQuery =  {
          search: searchQuery.value || "",
          category: selectedCategories.value.join(','), 
          market: selectedMarkets.value.join(','),
          brand: selectedBrands.value.join(','),
          client: selectedClients.value.join(','),
          page: options.value.page,
          options: JSON.stringify(options.value),
          ...params 
      }
      try {
          const response = await store
          .dispatch('app-skutopia/fetchProducts', paramsQuery)
          const { results, count } = response.data

          productsData.value = results
          totalProducts.value = count
          fetchFacets(paramsQuery)

      } catch (error) {
          console.log(error)
      } finally {
          loadProductData.value = false
      }
    }


    const fetchProductsInfinite = async ($state) => {
        
        const paramsQuery =  {
            search: searchQuery.value || "",
            category: selectedCategories.value.join(','), 
            market: selectedMarkets.value.join(','),
            brand: selectedBrands.value.join(','),
            page: options.value.page,
            itemsPerPage: options.value.itemsPerPage,
        }

        try {
            const response = await store.dispatch('app-skutopia/fetchProducts', paramsQuery)
            const { results, total_pages, count } = response.data
            
            const existingIds = new Set(productsData.value.map(p => p.id))
            const newProducts = results.filter(p => !existingIds.has(p.id))
            productsData.value.push(...newProducts)
            totalProducts.value = count 

            fetchFacets(paramsQuery)

            if (options.value.page < total_pages) {
                options.value.page += 1
                $state.loaded()
            } else {
                $state.complete()
            }
        } catch (error) {
            console.log(error)
            $state.complete()
        }
    }

    const fetchProduct = async (id) => {
      
      loadProductData.value = true
      await store.dispatch('app-skutopia/fetchProduct', id, {})
        .then(response => {
          loadProductData.value = false
          product.value = response.data
        })
        .catch(error => {
          loadProductData.value = false
          console.log(error)
        })
    }

    const updateProduct = async (sku) => {
      
      loadProductData.value = true
      return await store.dispatch('app-skutopia/updateProduct', sku)
        .then(response => {
          loadProductData.value = false
          return response.data
        })
        .catch(error => {
          loadProductData.value = false
          console.log(error)
        })
    }

    const deleteProduct = async (sku) => {
      
      loadProductData.value = true
      return await store.dispatch('app-skutopia/deleteProduct', sku)
        .then(response => {
          loadProductData.value = false
          productsData.value = []
          options.value.page = 1
          fetchProducts()
          return response.data
        })
        .catch(error => {
          loadProductData.value = false
          console.log(error)
        })
    }

    const addProduct = async (sku) => {
      
      loadProductData.value = true
      return await store.dispatch('app-skutopia/addProduct', sku)
        .then(response => {
          loadProductData.value = false
          return response.data
        })
        .catch(error => {
          loadProductData.value = false
          console.log(error)
        })
    }

    const fetchMarkets = async () => {
      
      marketLoading.value = true
      await store.dispatch('app-skutopia/fetchMarkets', {})
        .then(response => {
          marketOptions.value = response.data
          marketLoading.value = false
        })
        .catch(error => console.log(error))
    }

    const addBulkProductsInfo = async (file) => {
        loadProductData.value = true
        const formPOG = new FormData()
        formPOG.append('file', file)
        return await store.dispatch('app-skutopia/addBulkProductsInfo', formPOG)
          .then(response => {
              loadProductData.value = false
              return response.data
          })
          .catch(error => {
            loadProductData.value = false
            console.log(error)
          })
      }

    const addBulkProductImages = async (images) => {
        loadProductData.value = true
        const formPOG = new FormData()
        images.value.forEach((image, index) => {
          formPOG.append('images', image.file)
          const metadata = { name: image.file.name, side: image.side };
          formPOG.append(`metadata`, JSON.stringify(metadata));
        })
        return await store.dispatch('app-skutopia/addBulkProductImages', formPOG)
          .then(response => {
              loadProductData.value = false
              return response
          })
          .catch(error => {
            loadProductData.value = false
            return error.response
          })
      }

    const addBrand = async (payload) => {
      
      brandLoading.value = true
      await store.dispatch('app-skutopia/addBrand', payload)
        .then(() => fetchBrands())
        .catch(error => {
          brandLoading.value = false
          console.log(error)
        })
    }

    const fetchBrands = async () => {
      
      brandLoading.value = true
      await store.dispatch('app-skutopia/fetchBrands', {})
        .then(response => {
          brandOptions.value = response.data
          brandLoading.value = false
        })
        .catch(error => console.log(error))
    }

    const addCategory = async (payload) => {
      
      categoryLoading.value = true
      await store.dispatch('app-skutopia/addCategory', payload)
        .then(() => fetchCategories())
        .catch(error => {
          categoryLoading.value = false
          console.log(error)
        })
    }

    const fetchCategories = async () => {
      
      categoryLoading.value = true
      await store.dispatch('app-skutopia/fetchCategories', {})
        .then(response => {
          categoryOptions.value = response.data
          categoryLoading.value = false
        })
        .catch(error => console.log(error))
    }

    const fetchSpecs = async () => {
      
      specLoading.value = true
      store.dispatch('app-skutopia/fetchSpecs', {})
        .then(response => {
          specOptions.value = response.data
          specLoading.value = false
        })
        .catch(error => console.log(error))
    }

    const fetchBulkProducts = async (barcodes) => {
      try {
        
        BulkProductsLoading.value = true
        const response = await store.dispatch('app-skutopia/fetchBulkProducts', barcodes)
        return response 
      } catch (error) {
        console.error('Error fetching bulk products:', error)
        throw error 
      } finally {
        BulkProductsLoading.value = false
      }
    }

    const downloadImages = async (payload, filename=null) => {
        
        if (filename == null) filename = `Product Images.zip`
        downloadLoading.value = true
        store.dispatch('app-skutopia/downloadImages', payload)
        .then(response => {
            const { data } = response
            downloadLoading.value = false
            let fileURL = window.URL.createObjectURL(new Blob([data]));
            let fileLink = document.createElement('a');
            fileLink.href = fileURL;
            fileLink.setAttribute('download', filename);
            document.body.appendChild(fileLink);
            fileLink.click();
        })
        .catch(error => {
            downloadLoading.value = false
            console.log(error.response)
            store.commit('comp-alert/SET_MESSAGE', error.response.statusText)
        })
    }

  return {
    // Data & Options
    productsData,
    options,
    searchQuery,
    loadProductData,
    totalProducts,
    product,
    
    // Filters & Facets
    summaryCards,
    getFacetOptions,
    facets,     
    resetFilters,
    formatLabel,
    exportCatalog,
    selectedCategories,
    selectedMarkets,
    selectedProjects,
    selectedBrands,
    fetchFacets,
    fetchProductsInfinite,
    getLabelForValue,

    // Dropdown Data
    marketLoading,
    marketOptions,
    categoryOptions,
    categoryLoading,
    brandOptions,
    brandLoading,
    specOptions,
    specLoading,
    downloadLoading,
    BulkProductsLoading,

    // Actions
    downloadImages,
    fetchProducts,
    fetchProduct,
    fetchBrands,
    fetchMarkets,
    fetchCategories,
    fetchSpecs,
    deleteProduct,
    updateProduct,
    addProduct,
    addBrand,
    addCategory,
    addBulkProductsInfo,
    addBulkProductImages,
    fetchBulkProducts,

    icons: {
      mdiEarth,
      mdiShape,
      mdiTagOutline,
      mdiBriefcaseVariant,
    }
  }
}